import {
  Control,
  FieldErrors,
  useForm,
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import { isNullOrEmpty } from '../../../../util/string'
import React, { useEffect, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { CheckCpfResult, PixKeyType } from '@arland-bmnext/api-data'
import { FormBuilder, FormFieldConfig } from '../../../forms/FormBuilder'
import { CpfCheckForm } from '../CpfCheckForm'

enum CpfRegistrationFormState {
  CpfCheck,
  KYCForm,
}

type CpfRegistrationFormProps = {
  formFields: FormFieldConfig[]
  defaultValues
  onRegistrationFormCompleted
  errorMessage: string
  title: string
  description: string
  notifyFormIsDirty: () => void
  notifyPixKeyTypeChanged: (pixKeyType: PixKeyType) => void
}
export const CpfRegistrationForm = ({
  formFields,
  defaultValues,
  onRegistrationFormCompleted,
  errorMessage,
  title,
  description,
  notifyFormIsDirty,
  notifyPixKeyTypeChanged,
}: CpfRegistrationFormProps) => {
  const [registrationFormState, setRegistrationFormState] = useState<CpfRegistrationFormState>(
    CpfRegistrationFormState.CpfCheck,
  )
  const [cpf, setCpf] = useState<string>(undefined)

  const [currentFormFields, setCurrentFormFields] = useState(formFields)

  useEffect(() => {
    setCurrentFormFields([...formFields])
  }, [formFields])

  const {
    register,
    setValue,
    getValues,
    getFieldState,
    handleSubmit,
    control,
    trigger,
    formState: { isValid, errors, isDirty },
  } = useForm<any>({
    mode: 'onChange',
    shouldFocusError: true,
    values: defaultValues,
  })

  useEffect(() => {
    notifyFormIsDirty()
  }, [isDirty])

  useEffect(() => {
    const pixKeyType = getValues('pixKeyType')

    switch (pixKeyType) {
      case PixKeyType.Cpf:
        setValue('pixKey_cpf', cpf, {
          shouldValidate: true,
          shouldTouch: true,
        })
        break
      case PixKeyType.Email:
        const email = getValues('emailAddresse') ?? ''
        setValue('pixKey_email', email, {
          shouldValidate: true,
          shouldTouch: true,
        })
        break
      case PixKeyType.Telefone:
        const phoneNumber = getValues('phoneNumber') ?? ''
        setValue('pixKey_phone', phoneNumber, {
          shouldValidate: true,
          shouldTouch: true,
        })
        break
      default:
        setValue('pixKey', undefined)
        break
    }

    notifyPixKeyTypeChanged(!isNullOrEmpty(pixKeyType) ? parseInt(pixKeyType) : undefined)
  }, [getValues('pixKeyType')])

  const onCpfCheckFormCompleted = (cpfRequestData: CheckCpfResult) => {
    let firstName = cpfRequestData.nationalIdentity.firstName?.trim()
    let lastName = cpfRequestData.nationalIdentity.lastName?.trim()
    let birthday = cpfRequestData.nationalIdentity.birthDate

    const splitFirstName = firstName?.split(' ')
    const splitLastName = lastName?.split(' ')

    let changed = false
    if (splitFirstName?.length > 1) {
      firstName = splitFirstName[0]
      splitFirstName.shift()
      lastName = [...splitFirstName, ...splitLastName].join(' ')
    }

    if (!isNullOrEmpty(firstName)) {
      setValue('firstName', firstName, {
        shouldValidate: true,
        shouldTouch: true,
      })
      const firstNameField = currentFormFields.find((ff) => ff.property === 'firstName')
      if (firstNameField) {
        const idx = currentFormFields.indexOf(firstNameField)
        currentFormFields[idx] = {
          ...firstNameField,
          disabled: true,
          masked: true,
        }
        changed = true
      }
    }
    if (!isNullOrEmpty(lastName)) {
      setValue('lastName', lastName, {
        shouldValidate: true,
        shouldTouch: true,
      })
      const lastNameField = currentFormFields.find((ff) => ff.property === 'lastName')

      if (lastNameField) {
        const idx = currentFormFields.indexOf(lastNameField)
        currentFormFields[idx] = {
          ...lastNameField,
          disabled: true,
          masked: true,
        }
        changed = true
      }
    }
    if (!isNullOrEmpty(birthday) && (birthday as any) !== '0001-01-01T00:00:00') {
      setValue('birthday', cpfRequestData.nationalIdentity.birthDate, {
        shouldValidate: true,
        shouldTouch: true,
      })
      const birthdayField = currentFormFields.find((ff) => ff.property === 'birthday')

      if (birthdayField) {
        const idx = currentFormFields.indexOf(birthdayField)
        currentFormFields[idx] = {
          ...birthdayField,
          disabled: true,
        }
        changed = true
      }
    }

    if (changed) {
      setCurrentFormFields([...currentFormFields])
    }

    setCpf(cpfRequestData.nationalIdentity.nationalId)
    setRegistrationFormState(CpfRegistrationFormState.KYCForm)
  }

  const onKYCFormCompleted = (formData: any) => {
    onRegistrationFormCompleted({ ...formData, nationalIdentityId: cpf })
  }

  return (
    <>
      {registrationFormState === CpfRegistrationFormState.CpfCheck && (
        <CpfCheckForm onCpfCheckCompleted={onCpfCheckFormCompleted} title={title} description={description} />
      )}

      {registrationFormState === CpfRegistrationFormState.KYCForm && (
        <KYCForm
          onKYCFormCompleted={onKYCFormCompleted}
          register={register}
          setValue={setValue}
          getValues={getValues}
          control={control}
          errors={errors}
          trigger={trigger}
          handleSubmit={handleSubmit}
          isValid={isValid}
          getFieldState={getFieldState}
          formFields={currentFormFields}
          defaultValues={defaultValues}
          errorMessage={errorMessage}
          title={title}
          description={description}
        />
      )}
    </>
  )
}

type KYCFormProps = {
  onKYCFormCompleted: (formData: any) => void
  formFields: FormFieldConfig[]
  handleSubmit: any
  isValid: boolean
  defaultValues: any
  register: UseFormRegister<any>
  setValue: UseFormSetValue<any>
  getValues: UseFormGetValues<any>
  trigger: UseFormTrigger<any>
  control: Control<any, any>
  getFieldState: UseFormGetFieldState<any>
  title?: string
  description?: string
  errors: FieldErrors
  errorMessage?: string
}

const KYCForm = ({
  onKYCFormCompleted,
  register,
  setValue,
  getValues,
  control,
  errors,
  handleSubmit,
  isValid,
  getFieldState,
  formFields,
  defaultValues,
  errorMessage,
  title,
  description,
}: KYCFormProps) => {
  const { t } = useTranslation('register')

  const onSubmit = (formData) => {
    onKYCFormCompleted(formData)
  }

  return (
    <FormBuilder
      _register={register}
      _setValue={setValue}
      _getValues={getValues}
      _control={control}
      _errors={errors}
      _handleSubmit={handleSubmit}
      _isValid={isValid}
      _getFieldState={getFieldState}
      _fields={formFields}
      _defaultValues={defaultValues}
      _onSubmit={onSubmit}
      _submitText={t('RegisterPage.submit')}
      _errorMessage={errorMessage}
      _isMultiStepForm
      _multiStepFormFirstStepText={title ? title : t('RegisterPage.firstStepText')}
      _multiStepFormFirstStepDescription={description ? description : t('RegisterPage.firstStepDescription')}
      _multiStepFormLastStepText={t('RegisterPage.lastStepText')}
    />
  )
}
